import type { IMenuItemsPropsSDK } from '@wix/editor-elements-corvid-utils/src/props-factories/menuItemsPropsSDKFactory';
import { validateMenuItemsId } from '@wix/editor-elements-corvid-utils/src/props-factories/menuUtils/validators/validateMenuItemsId';
import { validateMenuItemsTarget } from '@wix/editor-elements-corvid-utils/src/props-factories/menuUtils/validators/validateMenuItemsTarget';
import { validateMenuItemsDepth } from '@wix/editor-elements-corvid-utils/src/props-factories/menuUtils/validators/validateMenuItemsDepth';
import type { PageList } from '@wix/editor-elements-corvid-utils/src/props-factories/menuUtils';
import {
  getLabel,
  getLink,
  InvalidLabelError,
  LinkTypeError,
} from '@wix/editor-elements-corvid-utils/src/props-factories/menuUtils';
import { assert } from '@wix/editor-elements-corvid-utils/src/assert';
import type { LinkUtils } from '@wix/thunderbolt-symbols';
import type { MenuDataItem } from '@wix/editor-elements-definitions';
import type { MenuItemType } from './Menu.types';

export const validators = [
  validateMenuItemsDepth(1),
  validateMenuItemsTarget,
  validateMenuItemsId,
];

// TODO: schema validation?
export const validateMenuItems = (menuItems: Array<MenuDataItem>) => {
  let isValid = true;
  isValid = validators.every(validator => validator(menuItems));
  return isValid;
};

const getMenuItems = (
  linkUtils: LinkUtils,
  pageList: PageList,
  items?: IMenuItemsPropsSDK['menuItems'],
) => {
  if (assert.isArray(items)) {
    return items.map((menuItem, i) =>
      createMenuDataItem(menuItem, i, linkUtils, pageList),
    );
  }
  return [];
};

export const createMenuDataItem = (
  menuDataItem: IMenuItemsPropsSDK['menuItems'][number],
  index: number,
  linkUtils: LinkUtils,
  pageList: PageList,
): IMenuItemsPropsSDK['menuItems'][number] => {
  const menuSdkItem: IMenuItemsPropsSDK['menuItems'][number] = {};

  try {
    const linkData = getLink({
      linkUtils,
      link: menuDataItem.link,
      target: menuDataItem.target || '_self',
    });

    if (linkData.href) {
      menuSdkItem.link = linkData.href;
      menuSdkItem.target = linkData.target || '_self';
    }
  } catch (error) {
    throw new LinkTypeError(menuDataItem.link || '', index);
  }

  const label = getLabel({
    label: menuDataItem.label,
    link: menuDataItem.link,
    pageList: pageList as PageList,
  });

  if (assert.isNil(label)) {
    throw new InvalidLabelError(index);
  }

  if (!assert.isNil(menuDataItem.id)) {
    menuSdkItem.id = menuDataItem.id;
  }

  menuSdkItem.label = label;

  if (!assert.isNil(menuDataItem.selected)) {
    menuSdkItem.selected = menuDataItem.selected;
  }

  return {
    ...menuSdkItem,
    menuItems: getMenuItems(linkUtils, pageList, menuDataItem.menuItems),
  };
};

/**
 * Calculate the index of the dropdown to open based on priority:
 * 1. Dropdown with most sub-items (item.items[].items)
 * 2. Dropdown with most items (item.items)
 * 3. First dropdown
 * @param items - Array of menu items
 * @returns Index of the dropdown to open, or -1 if no dropdown exists
 */
export const calculateDropdownIndexToOpen = (
  items: Array<MenuItemType> = [],
): number => {
  let selectedIndex = -1;
  let maxSubItemsCount = 0;
  let maxItemsCount = 0;
  let hasAnySubItems = false;

  items.forEach((item, index) => {
    if (item.items?.length) {
      const subItemsCount = item.items.reduce((sum, dropdownItem) => {
        return sum + (dropdownItem.items?.length || 0);
      }, 0);

      if (subItemsCount > 0) {
        hasAnySubItems = true;
        if (subItemsCount > maxSubItemsCount) {
          maxSubItemsCount = subItemsCount;
          selectedIndex = index;
        }
      }

      if (!hasAnySubItems && item.items.length > maxItemsCount) {
        maxItemsCount = item.items.length;
        selectedIndex = index;
      }
    }
  });

  return selectedIndex;
};
